import { type Ref, computed, ref } from 'vue';

import type { SharingPartnerRow } from '../types';

export type PartnerTabFilterValues = Record<string, (string | number)[]>;

export const PARTNER_FILTER = 'partner';
export const OWNER_FILTER = 'owner';
export const TAGS_FILTER = 'tags';

export default function usePartnerTabFilters(rows: Ref<SharingPartnerRow[]>) {
  const searchTerm = ref('');
  const activeFilters = ref<PartnerTabFilterValues>({});

  /**
   * The search box narrows the partner name; the Partner filter picks specific
   * partners. They combine rather than widen, as do all the filters.
   */
  const filteredRows = computed(() => {
    let filtered = rows.value;

    if (searchTerm.value) {
      const search = searchTerm.value.toLowerCase();
      filtered = filtered.filter((row) =>
        row.partner.name.toLowerCase().includes(search),
      );
    }

    const partners = activeFilters.value[PARTNER_FILTER];
    if (partners?.length) {
      filtered = filtered.filter((row) => partners.includes(row.id));
    }

    const owners = activeFilters.value[OWNER_FILTER];
    if (owners?.length) {
      filtered = filtered.filter(
        (row) => !!row.owner && owners.includes(row.owner.user_id),
      );
    }

    const tags = activeFilters.value[TAGS_FILTER];
    if (tags?.length) {
      filtered = filtered.filter((row) =>
        row.tags.some((tag) => tags.includes(tag.id)),
      );
    }

    return filtered;
  });

  function applySearch(value: string) {
    searchTerm.value = value;
  }

  function applyFilters(values: PartnerTabFilterValues) {
    activeFilters.value = values;
  }

  return { filteredRows, applySearch, applyFilters };
}
